import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import type { FamilySearchIndexEntry } from "../services/api/family";
import { familySearchIndexQuery, forViewer, viewerIdOf } from "../services/queries";
import { familyChildMatches, familyChildName } from "../domain/family/familySearch";

/**
 * Org-wide family member → guardian index, for child-name search on the Users
 * page and for picking children on an Award.
 */
export function useFamilySearchIndex(enabled = true) {
  const { user, token } = useAuth();
  const orgId = user?.schoolId || user?.orgId;

  return useQuery({
    ...forViewer(familySearchIndexQuery(orgId, token), viewerIdOf(user)),
    enabled: Boolean(orgId && token && enabled),
  });
}

/** Map guardian userId → matched child display names for a query. */
export function matchGuardiansByChildQuery(
  entries: FamilySearchIndexEntry[],
  query: string
): Map<string, string[]> {
  const byGuardian = new Map<string, string[]>();
  if (!query.trim()) return byGuardian;

  for (const entry of entries) {
    if (!familyChildMatches(entry, query)) continue;

    const fullName = familyChildName(entry);
    for (const userId of entry.guardianUserIds) {
      const list = byGuardian.get(userId) || [];
      if (!list.includes(fullName)) list.push(fullName);
      byGuardian.set(userId, list);
    }
  }

  return byGuardian;
}
